import React, { useState, useMemo, useEffect } from "react";
import {
  Plus,
  X,
  Search,
  MapPin,
  Clock,
  Utensils,
  Users,
  Twitter,
  Instagram,
  CalendarCheck,
  ChevronDown,
  Unlock,
  Lock,
  Edit2,
  Trash2,
  ArrowRight,
  ArrowLeft,
  Image as ImageIcon,
  Zap,
  Heart,
  Coffee,
  Tag,
  Eye
} from "lucide-react";

// --- STYLING CONSTANTS ---
const BORDER = "border-[4px] md:border-[6px] border-black shadow-[6px_6px_0px_0px_rgba(0,0,0,1)] md:shadow-[12px_12px_0px_0px_rgba(0,0,0,1)]";
const CYCLAMEN_PINK = "#F56FA1";
const PRACTICE_BLUE = "#A2D2FF";
const SAKURA_BG = "#FFF5F7";

// --- microCMS CONFIG ---
const MICROCMS_CONFIG = {
  serviceDomain: "kobedynamo",
  apiKey: "on4l2AwTIHwJTKlqVhrEYeoG1nGEhEhDjMoQ",
};

// --- SAKURA PETAL COMPONENT ---
const SakuraBlizzard = () => {
  const petals = Array.from({ length: 30 });
  return (
    <div className="fixed inset-0 pointer-events-none overflow-hidden z-0">
      {petals.map((_, i) => {
        const size = Math.random() * 25 + 15;
        const left = Math.random() * 100;
        const delay = Math.random() * 10;
        const duration = Math.random() * 5 + 5;
        const opacity = Math.random() * 0.5 + 0.3;

        return (
          <div
            key={i}
            className="sakura-petal"
            style={{
              width: `${size}px`,
              height: `${size * 0.7}px`,
              left: `${left}%`,
              animationDelay: `${delay}s`,
              animationDuration: `${duration}s`,
              opacity: opacity,
              backgroundColor: i % 2 === 0 ? "#ffb7c5" : CYCLAMEN_PINK,
            }}
          />
        );
      })}
    </div>
  );
};

// --- FLOATING BACKGROUND ELEMENTS ---
const FloatingElements = () => {
  return (
    <div className="fixed inset-0 pointer-events-none z-0 overflow-hidden opacity-5">
      <div
        className="cool-logo-font absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-[60vw] md:text-[45vw] font-black select-none tracking-[0.1em]"
        style={{ color: CYCLAMEN_PINK }}
      >
        DYNAMO
      </div>
    </div>
  );
};

export default function App() {
  // Tailwind CDN injection
  useEffect(() => {
    if (!document.getElementById("tailwind-cdn")) {
      const script = document.createElement("script");
      script.id = "tailwind-cdn";
      script.src = "https://cdn.tailwindcss.com";
      document.head.appendChild(script);
    }
  }, []);

  const [currentPage, setCurrentPage] = useState("HOME");
  const [modalGuard, setModalGuard] = useState(false);

  // --- API DATA STATES ---
  const [events, setEvents] = useState([]); 
  const [circles, setCircles] = useState([]); 
  const [practices, setPractices] = useState([]); 
  const [isLoading, setIsLoading] = useState(true);

  // --- FETCH DATA FROM microCMS ---
  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      const headers = {
        "X-MICROCMS-API-KEY": MICROCMS_CONFIG.apiKey.trim(),
      };

      try {
        const circlesRes = await fetch(
          `https://${MICROCMS_CONFIG.serviceDomain}.microcms.io/api/v1/circles?limit=100`,
          { headers }
        );
        const circlesData = await circlesRes.json();

        const eventsRes = await fetch(
          `https://${MICROCMS_CONFIG.serviceDomain}.microcms.io/api/v1/events?limit=100`,
          { headers }
        );
        const eventsData = await eventsRes.json();

        const practicesRes = await fetch(
          `https://${MICROCMS_CONFIG.serviceDomain}.microcms.io/api/v1/practices?limit=100`,
          { headers }
        );
        const practicesData = await practicesRes.json();

        if (circlesData && circlesData.contents) {
          const mappedCircles = circlesData.contents.map(item => {
            const rawType = Array.isArray(item.cluborcircle) 
              ? (item.cluborcircle[0] || "") 
              : (item.cluborcircle || "");
            const rawCat = Array.isArray(item.category) 
              ? (item.category[0] || "その他") 
              : (item.category || "その他");
            
            let typeKey = "CIRCLE";
            if (String(rawType).includes("部活") || String(rawType).toUpperCase().includes("CLUB")) {
              typeKey = "CLUB";
            }

            return {
              id: item.id,
              name: item.title || "名称未設定",
              desc: item.description || "",
              imageUrl: item['main-image']?.url 
                ? `${item['main-image'].url}?auto=format,compress&w=1200&fit=max&q=80` 
                : "",
              instagramId: item.instagram_id || "",   
              xId: item.Xaccount || "", 
              type: typeKey, 
              displayType: rawType || "CIRCLE", 
              category: rawCat, 
              tags: Array.isArray(item.tags) ? item.tags : (item.tags ? item.tags.split(',') : [])
            };
          });
          setCircles(mappedCircles);
        }

        if (eventsData && eventsData.contents) {
          const mappedEvents = eventsData.contents.map(item => {
            const rawCat = Array.isArray(item.category) ? item.category[0] : item.category;
            const reservationRaw = item.reservation;
            let needsReservation = false;
            if (Array.isArray(reservationRaw)) {
              needsReservation = reservationRaw.some(val => val && String(val).includes("必須"));
            } else if (reservationRaw) {
              needsReservation = String(reservationRaw).includes("必須");
            }

            return {
              id: item.id,
              title: item.title || "無題のイベント",
              circle: item['circle-name'] || "DYNAMO公式",
              month: Number(item.month) || 0,
              date: Number(item.date) || 0,
              time: item.time || "TBA",
              place: item.place || "TBA",
              desc: item.detail || "詳細未記入", 
              needsReservation: needsReservation,
              category: String(rawCat || "CULTURE").toUpperCase(),
              xId: item.Xaccount || "", 
              instagramId: item.instagram || "" 
            };
          });
          setEvents(mappedEvents);
        }

        if (practicesData && practicesData.contents) {
          const mappedPractices = practicesData.contents.map(item => {
            const reservationRaw = item.reservation;
            const rawCat = Array.isArray(item.category) ? item.category[0] : item.category;
            let needsReservation = false;
            if (Array.isArray(reservationRaw)) {
              needsReservation = reservationRaw.some(val => val && String(val).includes("必須"));
            } else if (reservationRaw) {
              needsReservation = String(reservationRaw).includes("必須");
            }

            return {
              id: item.id,
              month: Number(item.month) || 0,
              date: Number(item.date) || 0,
              name: item.name || "名称不明の団体",
              time: item.time || "未定",
              place: item.place || "未定",
              requirements: item.requirements || "特になし",
              needsReservation: needsReservation,
              category: String(rawCat || "CULTURE").toUpperCase()
            };
          });
          setPractices(mappedPractices);
        }

      } catch (error) {
        console.error("[ERROR] 同期中にエラー:", error.message);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [currentPage]);

  // --- UI STATES ---
  const [selectedDay, setSelectedDay] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [activeCategory, setActiveCategory] = useState(null);
  const [activeTypeFilter, setActiveTypeFilter] = useState(null); // 新規：EVENT または PRACTICE での絞り込み
  const [visibleSnsCircleId, setVisibleSnsCircleId] = useState(null);
  const [circleSearchTerm, setCircleSearchTerm] = useState("");
  const [activeCircleType, setActiveCircleType] = useState(null); 
  const [activeCircleCategory, setActiveCircleCategory] = useState(null); 
  const [visibleCirclesCount, setVisibleCirclesCount] = useState(10);
  const [visibleEventsLimit, setVisibleEventsLimit] = useState(5);
  const [visiblePracticesLimit, setVisiblePracticesLimit] = useState(5);
  const [visibleSnsEventId, setVisibleSnsEventId] = useState(null);

  // --- HANDLERS ---
  const handleOpenDay = (dayObj) => {
    setSelectedDay(dayObj);
    setVisibleSnsEventId(null);
    setVisibleEventsLimit(5); 
    setVisiblePracticesLimit(5); 
    setModalGuard(true);
    setTimeout(() => setModalGuard(false), 300);
  };

  // フィルタリング対象のイベント
  const matchingEvents = useMemo(() => {
    if (activeTypeFilter === 'PRACTICE') return []; // 練習のみフィルターなら空

    let filtered = events;
    if (searchTerm) {
      const lower = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (e) => (e.title || "").toLowerCase().includes(lower) || (e.circle && e.circle.toLowerCase().includes(lower)) || (e.category || "").toLowerCase().includes(lower)
      );
    }
    if (activeCategory) filtered = filtered.filter((e) => e.category === activeCategory);
    
    // フィルターが何も適用されていない（初期状態）なら、カレンダーには全件（totalCount）を出すために空を返す
    // 種類フィルターが 'EVENT' の場合は、全イベントを出す
    if (!searchTerm && !activeCategory && activeTypeFilter !== 'EVENT') return [];
    
    return filtered;
  }, [events, searchTerm, activeCategory, activeTypeFilter]);

  // フィルタリング対象の見学可能団体
  const matchingPractices = useMemo(() => {
    if (activeTypeFilter === 'EVENT') return []; // イベントのみフィルターなら空

    let filtered = practices;
    if (searchTerm) {
      const lower = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (p) => (p.name || "").toLowerCase().includes(lower) || (p.category || "").toLowerCase().includes(lower)
      );
    }
    if (activeCategory) filtered = filtered.filter((p) => p.category === activeCategory);
    
    if (!searchTerm && !activeCategory && activeTypeFilter !== 'PRACTICE') return [];

    return filtered;
  }, [practices, searchTerm, activeCategory, activeTypeFilter]);

  const filteredCirclesResult = useMemo(() => {
    if (!circles) return [];
    let res = circles;
    if (circleSearchTerm) {
      const lower = circleSearchTerm.toLowerCase();
      res = res.filter(
        (c) =>
          (c.name || "").toLowerCase().includes(lower) ||
          (Array.isArray(c.tags) && c.tags.some((t) => String(t).toLowerCase().includes(lower))) ||
          (c.desc || "").toLowerCase().includes(lower)
      );
    }
    if (activeCircleType) res = res.filter((c) => c.type === activeCircleType);
    if (activeCircleCategory) res = res.filter((c) => c.category === activeCircleCategory);
    return res;
  }, [circles, circleSearchTerm, activeCircleType, activeCircleCategory]);

  const allCircleCategories = useMemo(() => {
    const cats = circles.map(c => c.category).filter(Boolean);
    return Array.from(new Set(cats));
  }, [circles]);

  const displayedCircles = useMemo(
    () => filteredCirclesResult.slice(0, visibleCirclesCount),
    [filteredCirclesResult, visibleCirclesCount]
  );

  const renderMonthCalendar = (monthName, monthNum, daysCount, startOffset) => {
    const calendarDays = [];
    for (let i = 0; i < startOffset; i++) calendarDays.push(null);
    for (let i = 1; i <= daysCount; i++) calendarDays.push(i);
    while (calendarDays.length % 7 !== 0) calendarDays.push(null);

    return (
      <section key={monthName} className="mb-20 px-2 md:px-4 font-black">
        <div className="flex items-center justify-center mb-8 font-black">
          <div className="bg-black text-white px-6 py-2 border-[4px] border-white shadow-[6px_6px_0px_0px_#FFB7B2] transform -rotate-1">
            <h2 className="text-3xl md:text-5xl uppercase font-black leading-none">{monthName}</h2>
          </div>
        </div>
        <div className="grid grid-cols-7 border-[3px] md:border-[6px] border-black bg-black overflow-hidden shadow-[10px_10px_0px_0px_rgba(0,0,0,0.1)] max-w-5xl mx-auto font-black">
          {["S", "M", "T", "W", "T", "F", "S"].map((day, idx) => (
            <div key={`header-${monthNum}-${idx}`} className="p-4 text-center font-black bg-white text-black border-r-2 border-b-2 border-black text-sm md:text-2xl">{day}</div>
          ))}
          {calendarDays.map((dayNum, i) => {
            if (dayNum === null) return <div key={`empty-${monthNum}-${i}`} className="bg-white/20 border-r-2 border-b-2 border-black min-h-[75px] md:min-h-[140px]"></div>;
            
            const isFiltering = !!searchTerm || !!activeCategory || !!activeTypeFilter;
            
            const eventsOnDay = events.filter(e => Number(e.month) === Number(monthNum) && Number(e.date) === Number(dayNum));
            const matchCount = matchingEvents.filter(e => Number(e.month) === Number(monthNum) && Number(e.date) === Number(dayNum)).length;
            const displayCount = isFiltering ? matchCount : eventsOnDay.length;

            const practicesOnDay = practices.filter(p => Number(p.month) === Number(monthNum) && Number(p.date) === Number(dayNum));
            const practiceMatchCount = matchingPractices.filter(p => Number(p.month) === Number(monthNum) && Number(p.date) === Number(dayNum)).length;
            const displayPracticeCount = isFiltering ? practiceMatchCount : practicesOnDay.length;

            const isMatch = isFiltering && (matchCount > 0 || practiceMatchCount > 0);

            return (
              <div
                key={`day-${monthNum}-${dayNum}`}
                onClick={() => handleOpenDay({ month: monthNum, date: dayNum })}
                className={`min-h-[75px] md:min-h-[140px] p-2 md:p-6 border-r-2 border-b-2 border-black transition-all cursor-pointer relative group ${
                  isMatch ? "bg-[#FFF9C4] z-20 scale-[1.02]" : "bg-white md:hover:bg-pink-50 active:bg-pink-50"
                }`}
              >
                <span className={`text-2xl md:text-6xl font-black transition-colors ${isMatch ? "text-black" : (eventsOnDay.length > 0 || practicesOnDay.length > 0) ? "text-black" : "text-black/10"}`}>{dayNum}</span>
                {monthNum === 3 && dayNum === 10 && <div className="absolute top-1 left-1 md:top-4 md:left-4 animate-bounce font-black"><span className="text-xl md:text-5xl drop-shadow-[0_0_10px_#FFB7B2]">🌸</span></div>}
                {isMatch && <Zap className="absolute top-1 right-1 md:top-4 md:right-4 text-black animate-pulse w-5 h-5 md:w-10 md:h-10 fill-yellow-400 font-black" />}
                
                {displayCount > 0 && (
                  <div className={`absolute bottom-[44px] right-2 md:bottom-3 md:right-3 w-6 h-6 md:w-10 md:h-10 border-2 border-black flex items-center justify-center rotate-12 shadow-[3px_3px_0px_0px_white] ${isMatch ? "bg-black text-white" : "bg-[#FFB7B2] text-white font-black"}`}>
                    <span className="text-xs md:text-xl font-bold italic">{displayCount}</span>
                  </div>
                )}

                {displayPracticeCount > 0 && (
                  <div className={`absolute bottom-2 right-2 md:bottom-3 md:left-3 w-6 h-6 md:w-10 md:h-10 border-2 border-black flex items-center justify-center -rotate-12 shadow-[3px_3px_0px_0px_white] bg-[#A2D2FF] text-white font-black`}>
                    <span className="text-xs md:text-xl font-bold italic">{displayPracticeCount}</span>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </section>
    );
  };

  return (
    <div className="min-h-screen bg-[#FFF5F7] text-black overflow-x-hidden font-sans font-black">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Bebas+Neue&display=swap');
        .cool-logo-font { font-family: 'Bebas Neue', sans-serif; }
        body { background-color: #FFF5F7; margin: 0; font-family: sans-serif; font-weight: 900; }
        @keyframes marquee { 0% { transform: translateX(100%); } 100% { transform: translateX(-100%); } }
        .animate-marquee { animation: marquee 20s linear infinite; }
        .no-scrollbar::-webkit-scrollbar { display: none; }
        .no-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
        .sakura-petal { position: fixed; top: -30px; border-radius: 100% 0 100% 100%; animation: fall linear infinite; z-index: 10; }
        @keyframes fall { 0% { transform: translateY(0) rotate(0deg) translateX(0); } 25% { transform: translateY(25vh) rotate(90deg) translateX(30px); } 50% { transform: translateY(50vh) rotate(180deg) translateX(-30px); } 75% { transform: translateY(75vh) rotate(270deg) translateX(30px); } 100% { transform: translateY(105vh) rotate(360deg) translateX(-30px); } }
      `}</style>

      <div className="bg-black text-white font-black py-2 sticky top-0 z-[110] border-b-4 border-black text-[10px] md:text-sm overflow-hidden">
        <div className="whitespace-nowrap animate-marquee uppercase">LATEST DYNAMO INTEL: {events.length > 0 ? events[0].title : 'WELCOME TO KOBE DYNAMO'} • STAY READY • </div>
      </div>
      <FloatingElements />
      <nav className="p-4 md:p-8 flex justify-between items-center relative z-[100] font-black">
        <div className="flex items-center gap-4">
          <div onClick={() => setCurrentPage("HOME")} className="cool-logo-font bg-black text-white px-8 py-4 border-[4px] md:border-[5px] border-black shadow-[6px_6px_0px_0px_#000] transform -rotate-1 cursor-pointer active:scale-95 flex flex-col items-center leading-none">
            <span className="text-xl md:text-3xl tracking-[0.2em] mb-1">KOBE</span>
            <span className="text-2xl md:text-5xl text-[#F56FA1] tracking-[0.1em] transform rotate-1">DYNAMO</span>
          </div>
          {currentPage !== "HOME" && (
            <button onClick={() => setCurrentPage("HOME")} className="hidden md:flex bg-white text-black px-4 py-2 border-4 border-black shadow-[4px_4px_0px_0px_black] hover:bg-pink-100 active:translate-x-1 font-black">
              <ArrowLeft size={20} /> HOME
            </button>
          )}
        </div>
      </nav>

      {currentPage === "HOME" && (
        <main className="max-w-7xl mx-auto px-4 py-12 text-center relative z-10 animate-in fade-in duration-700 font-black">
          <SakuraBlizzard />
          <div className="mb-16 font-black">
            <h1 className="cool-logo-font text-[20vw] leading-[0.8] drop-shadow-[0_15px_40px_rgba(0,0,0,0.2)] uppercase tracking-[0.05em] select-none"><span className="inline-block transform -rotate-2">KOBE</span><br/><span className="text-[#F56FA1] inline-block transform rotate-1">DYNAMO</span></h1>
          </div>
          <h2 className="text-[7.2vw] md:text-[5.5vw] mb-20 italic leading-tight text-black font-bold font-black">あなたがあなたらしい<br/><span className="inline-block bg-white text-black px-10 py-3 transform skew-x-[-12deg] shadow-[8px_8px_0px_0px_#FFB7B2] not-italic my-4 border-4 border-black font-bold font-black">大学生</span><br/>になるために</h2>
          <div className="flex flex-col gap-12 md:gap-16 max-w-2xl mx-auto font-black">
            <div className="flex flex-col items-center w-full"><p className="text-3xl md:text-5xl text-black mb-8 uppercase italic font-bold">あなたの仲間を<br/>見つけよう</p>
              <button onClick={() => setCurrentPage("CALENDAR")} className={`w-full bg-[#FFF9C4] text-black px-10 py-12 ${BORDER} hover:bg-white transition-all transform hover:-translate-y-2 active:translate-y-0 font-black`}><span className="text-3xl md:text-6xl font-black uppercase italic tracking-tighter flex items-center justify-center gap-6">EVENT CALENDAR <ArrowRight size={48}/></span></button>
            </div>
            <div className="flex flex-col items-center w-full"><p className="text-3xl md:text-5xl text-black mb-8 uppercase italic font-bold">あなたの居場所を<br/>見つけよう</p>
              <button onClick={() => setCurrentPage("CIRCLES")} className={`w-full bg-[#A2D2FF] text-black px-10 py-12 ${BORDER} hover:bg-white transition-all transform hover:-translate-y-2 active:translate-y-0 font-black`}><span className="text-3xl md:text-6xl font-black uppercase italic tracking-tighter flex items-center justify-center gap-6">CLUB / CIRCLE LIST <Users size={48}/></span></button>
            </div>
            <div className="flex flex-col items-center w-full font-black"><p className="text-3xl md:text-5xl text-black mb-8 uppercase italic font-bold">あなたの行きつけを<br/>見つけよう</p>
              <button onClick={() => setCurrentPage("GOURMET")} className={`w-full bg-[#C1E1C1] text-black px-10 py-12 ${BORDER} hover:bg-white transition-all transform hover:-translate-y-2 active:translate-y-0 font-black`}><span className="text-3xl md:text-6xl font-black uppercase italic tracking-tighter flex items-center justify-center gap-6">GOURMET GUIDE <Coffee size={48}/></span></button>
            </div>
          </div>
        </main>
      )}

      {currentPage === "CALENDAR" && (
        <div className="animate-in slide-in-from-right duration-500 px-4 font-black">
          <header className="text-center py-12 font-black">
            <h1 className="cool-logo-font text-[12vw] md:text-[10vw] font-black text-black">CALENDAR</h1>
            
            {/* 修正：種類フィルターの追加 */}
            <div className="flex flex-wrap justify-center gap-4 mt-8 mb-4 px-2">
               <button onClick={() => setActiveTypeFilter(activeTypeFilter === 'EVENT' ? null : 'EVENT')}
                       className={`px-6 py-2 border-[4px] border-black font-black uppercase italic transition-all ${activeTypeFilter === 'EVENT' ? 'bg-[#FFB7B2] text-black shadow-none translate-x-1 translate-y-1' : 'bg-white text-black shadow-[4px_4px_0px_0px_black]'}`}>Events only</button>
               <button onClick={() => setActiveTypeFilter(activeTypeFilter === 'PRACTICE' ? null : 'PRACTICE')}
                       className={`px-6 py-2 border-[4px] border-black font-black uppercase italic transition-all ${activeTypeFilter === 'PRACTICE' ? 'bg-[#A2D2FF] text-black shadow-none translate-x-1 translate-y-1' : 'bg-white text-black shadow-[4px_4px_0px_0px_black]'}`}>Visits only</button>
            </div>

            <div className="flex flex-wrap justify-center gap-2 md:gap-4 mt-4 px-2">
              {['MUSIC', 'SPORTS', 'CULTURE', 'STUDY'].map(cat => (
                <button key={cat} onClick={() => setActiveCategory(activeCategory === cat ? null : cat)} className={`px-4 md:px-8 py-2 md:py-3 font-bold text-xs md:text-xl transition-all border-2 md:border-4 border-black uppercase italic ${activeCategory === cat ? 'bg-black text-white shadow-none translate-x-1 translate-y-1' : 'bg-white text-black shadow-[4px_4px_0px_0px_black]'}`}>{cat}</button>
              ))}
            </div>
            
            <div className="max-w-xl mx-auto mt-8 flex bg-white border-4 border-black p-2 font-black">
              <Search className="text-black m-2"/><input type="text" placeholder="SEARCH EVERYTHING..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="w-full bg-transparent text-black outline-none text-xl uppercase font-bold font-black"/>
            </div>
            <div className="flex justify-center gap-8 mt-6 font-black">
              <div className="flex items-center gap-3">
                <div className="w-6 h-6 bg-[#FFB7B2] border-2 border-black shadow-[3px_3px_0px_0px_black] rotate-12"></div>
                <span className="text-lg md:text-xl italic uppercase font-bold">：イベント</span>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-6 h-6 bg-[#A2D2FF] border-2 border-black shadow-[3px_3px_0px_0px_black] -rotate-12"></div>
                <span className="text-lg md:text-xl italic uppercase font-bold">：練習見学可能</span>
              </div>
            </div>
          </header>
          {renderMonthCalendar("March", 3, 31, 0)}
          {renderMonthCalendar("April", 4, 30, 3)}
          {renderMonthCalendar("May", 5, 31, 5)}
          <div className="flex justify-center mb-10 font-black"><button onClick={() => setCurrentPage("HOME")} className="bg-black text-white px-8 py-4 border-4 border-black font-bold text-2xl uppercase">BACK TO HOME</button></div>
        </div>
      )}

      {currentPage === "CIRCLES" && (
        <div className="max-w-4xl mx-auto px-4 py-12 animate-in slide-in-from-bottom duration-500 font-black">
          <header className="text-center mb-20 font-black">
            <h1 className="cool-logo-font text-[12vw] md:text-[10vw] font-black text-black uppercase">CLUB / CIRCLE</h1>
            <div className="flex flex-wrap justify-center gap-4 my-10 font-bold font-black">
              {['CLUB', 'CIRCLE'].map(t => (
                <button key={t} onClick={() => setActiveCircleType(activeCircleType === t ? null : t)} className={`px-8 py-3 border-4 border-black text-2xl font-bold italic uppercase transition-all ${activeCircleType === t ? "bg-black text-white shadow-none translate-x-1 translate-y-1" : "bg-white text-black shadow-[4px_4px_0px_0px_black]"}`}>{t}</button>
              ))}
            </div>
            <div className="flex flex-wrap justify-center gap-2 mb-10 px-2 font-bold font-black font-black">
              {allCircleCategories.map(cat => (
                <button key={cat} onClick={() => setActiveCircleCategory(activeCircleCategory === cat ? null : cat)} className={`px-4 py-2 border-2 border-black text-sm md:text-lg uppercase transition-all ${activeCircleCategory === cat ? 'bg-[#A2D2FF] text-black font-black' : 'bg-white text-black/60 hover:text-black hover:bg-zinc-100 font-black'}`}>#{cat}</button>
              ))}
            </div>
            <div className="max-w-2xl mx-auto flex bg-white border-4 border-black p-2 font-black mt-8">
              <Search className="text-black m-2 font-black"/><input type="text" placeholder="SEARCH CLUBS..." value={circleSearchTerm} onChange={e => setCircleSearchTerm(e.target.value)} className="w-full bg-transparent text-black outline-none text-xl uppercase font-bold font-black"/>
            </div>
          </header>
          <div className="flex flex-col gap-12 font-black">
            {displayedCircles.map((c) => (
              <div key={c.id} className={`${BORDER} bg-white p-6 md:p-10 relative overflow-hidden flex flex-col font-black`}>
                {c.imageUrl && (
                  <div className="w-full aspect-[4/3] mb-8 overflow-hidden border-[4px] border-black shadow-[4px_4px_0px_0px_black] bg-zinc-800 flex items-center justify-center font-black">
                    <img 
                      src={c.imageUrl} 
                      alt={c.name} 
                      className="max-w-full max-h-full object-contain font-black" 
                      loading="lazy"
                    />
                  </div>
                )}
                <div className="flex justify-between items-start mb-6 font-black">
                  <div className="max-w-[80%]">
                    <h3 className="text-3xl md:text-5xl font-black text-black tracking-tight break-words leading-tight">{c.name}</h3>
                    <div className="flex items-center gap-2 mt-2 font-bold text-[#F56FA1]">
                      <Tag size={16}/>
                      <span className="text-sm md:text-xl uppercase tracking-tighter italic">{c.category}</span>
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-2">
                    <span className="bg-black text-white px-3 py-1 text-xs md:text-lg italic font-bold uppercase">{c.displayType}</span>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2 mb-6 font-bold font-black">
                  {Array.isArray(c.tags) && c.tags.map((tag) => <span key={tag} className="text-black border-2 border-black px-2 py-1 text-xs font-bold">#{tag}</span>)}
                </div>
                <p className="text-black/70 text-xl md:text-2xl italic mb-10 leading-relaxed font-bold whitespace-pre-wrap break-words">"{c.desc}"</p>
                <div className="mt-auto space-y-4 font-bold">
                  <button onClick={() => setVisibleSnsCircleId(visibleSnsCircleId === c.id ? null : c.id)} className={`w-full py-4 border-4 border-black font-black text-xl uppercase transition-all ${visibleSnsCircleId === c.id ? "bg-black text-white shadow-none" : "hover:bg-[#A2D2FF] hover:text-black"}`}>Social Channels</button>
                  {visibleSnsCircleId === c.id && (
                    <div className="grid grid-cols-1 gap-4 animate-in slide-in-from-top-2">
                      <div className="bg-white p-4 border-2 border-black text-sm truncate font-bold flex flex-col gap-2">
                        <div className="flex items-center gap-2"><Instagram size={20}/> @{c.instagramId || "NONE"}</div>
                        <div className="flex items-center gap-2"><Twitter size={20}/> @{c.xId || "NONE"}</div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}
            {filteredCirclesResult.length > visibleCirclesCount && (
              <div className="flex justify-center mt-10">
                <button onClick={() => setVisibleCirclesCount(prev => prev + 10)} className="bg-black text-white px-10 py-5 border-4 border-black font-black text-2xl uppercase shadow-[6px_6px_0px_0px_rgba(0,0,0,0.3)] hover:bg-[#F56FA1] transition-all font-black">SEE MORE CLUBS (+10)</button>
              </div>
            )}
          </div>
          <div className="flex justify-center mt-10 font-black"><button onClick={() => setCurrentPage("HOME")} className="bg-black text-white px-8 py-4 border-4 border-black font-bold text-2xl uppercase">BACK TO HOME</button></div>
        </div>
      )}

      {currentPage === "GOURMET" && (
        <div className="min-h-[80vh] flex flex-col items-center justify-center p-4 font-black">
          <div className="text-center animate-in zoom-in duration-500 font-black">
             <div className="inline-block p-6 bg-[#C1E1C1] border-8 border-black shadow-[12px_12px_0px_0px_black] mb-12 transform -rotate-2">
                <Utensils size={80} className="mx-auto mb-4"/>
                <h1 className="cool-logo-font text-6xl md:text-8xl">COMING SOON</h1>
             </div>
             <p className="text-2xl md:text-4xl uppercase font-bold italic mb-12 font-black">あなたの行きつけを見つけよう。<br/>現在データを収集中です。</p>
             <button onClick={() => setCurrentPage("HOME")} className="bg-black text-white px-12 py-6 border-4 border-black font-bold text-2xl uppercase hover:bg-white hover:text-black transition-all">BACK TO HOME</button>
          </div>
        </div>
      )}

      {selectedDay && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 font-black">
          <div className="absolute inset-0 bg-black/60 md:backdrop-blur-sm" onClick={() => setSelectedDay(null)}></div>
          <div className={`w-full max-w-2xl bg-white border-4 border-black relative z-10 p-6 md:p-12 max-h-[85vh] overflow-y-auto ${BORDER}`}>
            <div className="flex justify-between items-center mb-10 border-b-4 border-white pb-6 font-bold">
              <h2 className="text-5xl md:text-7xl text-black italic">{selectedDay.month}/{selectedDay.date}</h2>
              <div className="flex gap-2"><button onClick={() => setSelectedDay(null)} className="bg-white p-2 text-black border-4 border-black hover:bg-[#FFB7B2] transition-all"><X size={40}/></button></div>
            </div>
            
            <div className="space-y-12 font-bold text-black">
              {/* --- EVENTS SECTION --- */}
              {(() => {
                const dailyEvents = (!!searchTerm || !!activeCategory || !!activeTypeFilter ? matchingEvents : events).filter(e => Number(e.month) === Number(selectedDay.month) && Number(e.date) === Number(selectedDay.date));
                if (dailyEvents.length > 0) return (
                  <div className="space-y-8">
                    <h4 className="text-2xl md:text-3xl font-black uppercase italic border-b-4 border-black pb-2 flex items-center gap-2"><Zap className="fill-yellow-400" size={24}/> UPCOMING EVENTS</h4>
                    {dailyEvents.slice(0, visibleEventsLimit).map((event) => (
                      <div key={event.id} className={`${event.category === 'SPORTS' ? 'bg-[#A2D2FF]' : event.category === 'CULTURE' ? 'bg-[#FFF9C4]' : 'bg-[#FFB7B2]'} p-8 border-4 border-black text-black font-bold`}>
                        <div className="flex justify-between font-bold">
                          <div className="flex gap-3 flex-wrap items-center">
                            <span className="bg-black text-white px-5 py-2 text-xl md:text-2xl font-black italic break-words max-w-full inline-block">{event.circle}</span>
                            <div className="flex items-center gap-1 bg-white/40 px-3 py-1 border-2 border-black rounded-full font-bold">
                              <Tag size={16} />
                              <span className="text-sm md:text-lg uppercase tracking-tighter">{event.category}</span>
                            </div>
                          </div>
                        </div>
                        <h3 className="text-3xl md:text-5xl my-6 font-black break-words leading-tight">{event.title}</h3>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 text-2xl md:text-3xl font-bold">
                          <div className="flex items-center gap-3"><Clock size={28}/>{event.time}</div>
                          <div className="flex items-center gap-3"><MapPin size={28}/>{event.place}</div>
                        </div>
                        <p className="mt-8 text-xl border-l-[10px] border-black pl-4 italic font-bold whitespace-pre-wrap break-words leading-relaxed">"{event.desc}"</p>
                        
                        <div className="mt-10 flex flex-wrap gap-4 font-bold">
                          <div className={`flex-1 flex items-center justify-center px-4 py-3 border-4 border-black font-black text-xl md:text-2xl transition-all shadow-[4px_4px_0px_0px_black] ${event.needsReservation ? "bg-red-500 text-white" : "bg-[#C1E1C1] text-black"}`}>
                             {event.needsReservation ? "予約必須" : "予約不要"}
                          </div>
                          <button onClick={() => setVisibleSnsEventId(visibleSnsEventId === event.id ? null : event.id)} className="flex-1 py-4 bg-black text-white border-4 border-black font-black text-xl uppercase">SNS INFO</button>
                        </div>
                        {visibleSnsEventId === event.id && (
                          <div className="mt-4 flex gap-4 animate-in slide-in-from-top-2">
                            <div className="flex flex-col gap-2 bg-black/10 p-4 border-4 border-black flex-1 font-bold">
                              <div className="flex items-center gap-2"><Instagram size={20}/> @{event.instagramId || "NONE"}</div>
                              <div className="flex items-center gap-2"><Twitter size={20}/> @{event.xId || "NONE"}</div>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                    {dailyEvents.length > visibleEventsLimit && (<button onClick={() => setVisibleEventsLimit(prev => prev + 5)} className="w-full bg-black text-white py-4 border-4 border-black font-black text-xl uppercase hover:bg-[#F56FA1] transition-all">LOAD MORE EVENTS (+5)</button>)}
                  </div>
                );
              })()}

              {/* --- PRACTICES (見学可能団体) SECTION --- */}
              {(() => {
                const dailyPractices = (!!searchTerm || !!activeCategory || !!activeTypeFilter ? matchingPractices : practices).filter(p => Number(p.month) === Number(selectedDay.month) && Number(p.date) === Number(selectedDay.date));
                if (dailyPractices.length > 0) return (
                  <div className="space-y-8 pt-4 font-black">
                    <h4 className="text-2xl md:text-3xl font-black uppercase italic border-b-4 border-[#A2D2FF] pb-2 flex items-center gap-2"><Eye className="text-[#A2D2FF]" size={24}/> PRACTICE / VISIT LIST</h4>
                    <div className="grid grid-cols-1 gap-6">
                      {dailyPractices.slice(0, visiblePracticesLimit).map((p) => (
                        <div key={p.id} className="bg-white p-6 md:p-8 border-[4px] border-black shadow-[6px_6px_0px_0px_#A2D2FF] font-black">
                          <div className="flex justify-between items-start mb-4">
                             <div className="max-w-[70%]">
                                <h5 className="text-2xl md:text-4xl font-black uppercase break-words leading-tight">{p.name}</h5>
                                <div className="flex items-center gap-2 mt-2 font-bold text-[#F56FA1]">
                                  <Tag size={16} />
                                  <span className="text-sm md:text-xl uppercase tracking-tighter italic">{p.category}</span>
                                </div>
                             </div>
                             <div className={`px-4 py-1 border-2 border-black font-bold text-sm uppercase ${p.needsReservation ? "bg-red-500 text-white" : "bg-[#C1E1C1] text-black"}`}>
                                {p.needsReservation ? "予約必須" : "予約不要"}
                             </div>
                          </div>
                          <div className="bg-zinc-50 p-4 border-2 border-black flex flex-col gap-2">
                             <div className="flex flex-wrap gap-4 text-black/60 font-black">
                                <div className="flex items-center gap-2"><Clock size={16}/><span className="text-lg md:text-xl font-bold">{p.time}</span></div>
                                <div className="flex items-center gap-2"><MapPin size={16}/><span className="text-lg md:text-xl font-bold">{p.place}</span></div>
                             </div>
                             <div className="flex flex-col gap-1 font-black">
                                <span className="text-xs uppercase font-bold text-black/40">requirements:</span>
                                <p className="text-lg md:text-xl font-bold italic">"{p.requirements}"</p>
                             </div>
                          </div>
                        </div>
                      ))}
                    </div>
                    {dailyPractices.length > visiblePracticesLimit && (<button onClick={() => setVisiblePracticesLimit(prev => prev + 5)} className="w-full bg-black text-white py-4 border-4 border-black font-black text-xl uppercase hover:bg-[#A2D2FF] hover:text-black transition-all">SEE MORE VISITS (+5)</button>)}
                  </div>
                );
              })()}
            </div>
          </div>
        </div>
      )}

      <footer className="bg-white text-black py-20 text-center relative z-20 border-t-[8px] border-black mt-40 font-bold font-black">
        <h4 className="text-[15vw] font-black leading-none uppercase tracking-tighter opacity-10 select-none">KOBE FORCE</h4>
        <p className="font-bold text-sm md:text-xl uppercase tracking-widest opacity-40 font-black">© 2026 KOBE DYNAMO PROJECT</p>
      </footer>
      <div className="h-40 bg-white w-full relative z-20 font-black font-black font-black font-black"></div>
    </div>
  );
}

